import { validateRegex } from '~/util/validate';
/** Name of the global an embedder can set to inject presets at runtime. */
export const PRESET_GLOBAL_NAME = '__AW_PRESET_CATEGORY_SETS__';
function isPlainObject(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function parseRule(raw, context) {
    if (!isPlainObject(raw)) {
        console.warn(`[presets] ${context}: category is missing a rule, skipping`);
        return null;
    }
    // `null` was a valid "no rule" type in older exports
    const type = raw.type === null || raw.type === undefined ? 'none' : raw.type;
    if (type === 'none') {
        return { type: 'none' };
    }
    if (type !== 'regex') {
        console.warn(`[presets] ${context}: unknown rule type ${JSON.stringify(type)}, skipping`);
        return null;
    }
    if (typeof raw.regex !== 'string' || raw.regex.length === 0) {
        console.warn(`[presets] ${context}: regex rule without a pattern, skipping`);
        return null;
    }
    // validateRegex() also rejects patterns that only work in JavaScript: rules
    // are sent to the server-side query engine via `classes_for_query`, so a
    // JS-only pattern would break every categorized query.
    if (!validateRegex(raw.regex)) {
        console.warn(`[presets] ${context}: invalid regex ${JSON.stringify(raw.regex)}, skipping`);
        return null;
    }
    const rule = { type: 'regex', regex: raw.regex };
    if (raw.ignore_case === true)
        rule.ignore_case = true;
    const priority = parsePresetPriority(raw, context);
    if (priority === null)
        return null;
    if (priority !== undefined)
        rule.priority = priority;
    // Inlined from classes.normalizeSelectKeys to avoid a runtime cycle
    // (classes.ts imports this module). Empty/duplicate lists are dropped so
    // the rust parser never sees `select_keys: []`.
    const selectKeys = normalizePresetSelectKeys(raw.select_keys);
    if (selectKeys)
        rule.select_keys = selectKeys;
    return rule;
}
function parsePresetPriority(raw, context) {
    const value = raw.priority !== undefined ? raw.priority : raw.weight;
    if (value === undefined) {
        return undefined;
    }
    if (typeof value !== 'number' || !Number.isInteger(value)) {
        console.warn(`[presets] ${context}: priority/weight must be an integer, skipping`);
        return null;
    }
    return value;
}
function normalizePresetSelectKeys(keys) {
    if (!Array.isArray(keys) || keys.length === 0) {
        return undefined;
    }
    const unique = [];
    for (const key of keys) {
        if (typeof key === 'string' && key && !unique.includes(key)) {
            unique.push(key);
        }
    }
    return unique.length > 0 ? unique : undefined;
}
function parseCategory(raw, context) {
    if (!isPlainObject(raw)) {
        console.warn(`[presets] ${context}: category is not an object, skipping`);
        return null;
    }
    const name = raw.name;
    if (!Array.isArray(name) ||
        name.length === 0 ||
        !name.every(n => typeof n === 'string' && n.length > 0)) {
        console.warn(`[presets] ${context}: category name must be a non-empty list of strings`);
        return null;
    }
    const rule = parseRule(raw.rule, `${context} category ${name.join('>')}`);
    if (rule === null)
        return null;
    const category = { name: name, rule };
    if (isPlainObject(raw.data))
        category.data = { ...raw.data };
    return category;
}
function parseSet(raw, index) {
    if (!isPlainObject(raw)) {
        console.warn(`[presets] set #${index} is not an object, skipping`);
        return null;
    }
    if (typeof raw.id !== 'string' || raw.id.length === 0) {
        console.warn(`[presets] set #${index} is missing a non-empty string id, skipping`);
        return null;
    }
    if (!Array.isArray(raw.categories)) {
        console.warn(`[presets] set "${raw.id}" has no categories list, skipping`);
        return null;
    }
    const categories = raw.categories
        .map(c => parseCategory(c, `set "${raw.id}"`))
        .filter((c) => c !== null);
    if (categories.length === 0) {
        console.warn(`[presets] set "${raw.id}" has no valid categories, skipping`);
        return null;
    }
    return { id: raw.id, categories };
}
/**
 * Parse and validate a preset payload.
 *
 * Accepts a JSON string, a single CategorySet, or a list of CategorySets.
 * Invalid entries are dropped (with a warning); never throws.
 */
export function parsePresetCategorySets(raw) {
    if (raw === undefined || raw === null)
        return [];
    let value = raw;
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.length === 0)
            return [];
        try {
            value = JSON.parse(trimmed);
        }
        catch (e) {
            console.error('[presets] failed to parse preset category sets as JSON, ignoring', e);
            return [];
        }
    }
    const rawSets = Array.isArray(value) ? value : [value];
    const sets = [];
    const seen = new Set();
    rawSets.forEach((rawSet, i) => {
        const set = parseSet(rawSet, i);
        if (set === null)
            return;
        if (seen.has(set.id)) {
            console.warn(`[presets] duplicate set id "${set.id}", keeping the first one`);
            return;
        }
        seen.add(set.id);
        sets.push(set);
    });
    return sets;
}
function readRuntimeGlobal() {
    if (typeof globalThis === 'undefined')
        return undefined;
    return globalThis[PRESET_GLOBAL_NAME];
}
function readBuildConstant() {
    // Guarded with typeof: the constant is only defined in builds that set it
    return typeof AW_PRESET_CATEGORY_SETS !== 'undefined' ? AW_PRESET_CATEGORY_SETS : undefined;
}
let cached = null;
/**
 * The preset category sets this build/deployment ships, if any.
 *
 * Returns an empty list for stock builds, in which case all preset-related
 * behaviour is inert.
 */
export function getPresetCategorySets() {
    if (cached === null) {
        const runtime = readRuntimeGlobal();
        const source = runtime !== undefined && runtime !== null ? runtime : readBuildConstant();
        cached = parsePresetCategorySets(source);
        if (cached.length > 0) {
            console.info('[presets] loaded preset category sets:', cached.map(s => s.id).join(', '));
        }
    }
    // Deep enough copy to keep callers from mutating the cache
    return cached.map(s => ({
        id: s.id,
        categories: s.categories.map(c => ({ ...c, name: [...c.name], rule: { ...c.rule } })),
    }));
}
/** Drop the memoized presets. Intended for tests. */
export function clearPresetCategorySetsCache() {
    cached = null;
}
